import styled, { css, keyframes } from 'styled-components';
import { MODAL_PHONE_MAX_WIDTH_PX, type ModalSize } from './Modal.constants';

const PHONE = `@media (max-width: ${MODAL_PHONE_MAX_WIDTH_PX}px)`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme: { spacing } }) => spacing?.level5 ?? '16px'};
  /* 0.7 alpha matches the design system's modal scrim — the lighter
     0.55 we used previously didn't dim the page enough for the panel
     (which now sits at --surface, not --surfaceContainerHigh) to read
     as a lifted dialog. */
  background: rgb(0 0 0 / 0.7);
  animation: ${fadeIn} ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'}
    ${({ theme: { motion } }) => motion?.easing?.standard ?? 'ease-out'};
  box-sizing: border-box;

  ${PHONE} {
    padding: 0 ${({ theme: { spacing } }) => spacing?.level3 ?? '8px'};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const sizeWidth = (size: ModalSize): string => {
  switch (size) {
    case 'sm':
      return '420px';
    case 'lg':
      return '720px';
    case 'xl':
      return '960px';
    case 'md':
    default:
      return '560px';
  }
};

export const Panel = styled.div<{ $size: ModalSize }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${({ $size }) => sizeWidth($size)};
  max-height: calc(100vh - 64px);
  /* Design system uses 22px corner radius (was 16). */
  border-radius: 22px;
  outline: none;
  /* All internal spacing comes from this single panel padding — Header,
     Body, and Footer are unpadded boxes that sit inside it. Matches the
     design's wf-details rule (padding 28px 36px 24px). */
  padding: 28px 36px 24px;
  overflow: hidden;
  animation: ${popIn} ${({ theme: { motion } }) => motion?.duration?.base ?? '200ms'}
    ${({ theme: { motion } }) => motion?.easing?.emphasised ?? 'cubic-bezier(0.2,0.8,0.2,1)'};

  /* Panel sits at --surface (not --surfaceContainerHigh) and has no
     hairline border — the strong 0.7 backdrop is what separates the
     dialog from the page. Matches the design system's wf-details and
     npm-wf-modal patterns. */
  ${({ theme: { colors = {}, elevation } }) => css`
    background: ${colors.surface ?? '#13131B'};
    color: ${colors.onSurface ?? '#E4E1ED'};
    border: 0;
    box-shadow: ${elevation?.modal ?? '0 24px 60px rgba(0, 0, 0, 0.45)'};
  `}

  ${PHONE} {
    padding: 20px 16px;
    max-height: 100dvh;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme: { spacing } }) => spacing?.level4 ?? '12px'};
  /* No padding, no border — panel padding handles the outer gutter and
     this margin handles vertical rhythm above/below the title block.
     18px top + 28px panel padding-top = 46px from panel edge to title.
     Matches the design's wf-details title rule (margin 18px 0 18px). */
  margin: 18px 0;
`;

/**
 * 36x36 leading icon slot rendered before the title block. Uses the M3
 * "soft accent" treatment — a translucent primary tint behind the primary
 * colour itself — rather than a solid primary-container fill, so the glyph
 * reads as a quiet badge instead of a hard chrome button.
 */
export const HeaderIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.small ?? '8px'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${({ theme: { colors = {} } }) => css`
    background: color-mix(
      in oklch,
      ${colors.primary ?? '#C1C1FF'} 16%,
      transparent
    );
    color: ${colors.primary ?? '#C1C1FF'};
  `}

  & > svg {
    width: 20px;
    height: 20px;
  }
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
`;

export const TitleEl = styled.h2`
  font-size: 17px;
  /* 700 matches the design's wf-details title weight. */
  font-weight: 700;
  line-height: 1.25;
  /* No margin — the Header wrapper provides vertical rhythm so icon and
     title stay aligned via flex align-items. */
  margin: 0;
  letter-spacing: -0.01em;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E4E1ED'};
`;

export const Subtitle = styled.p`
  font-size: 13px;
  font-weight: 400;
  margin: 0;
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#C7C4D7'};
`;

export const CloseButton = styled.button`
  /* Absolutely positioned over the panel padding — matches the design's
     wf-details close rule (position absolute, top 16px, right 16px).
     Sits OUTSIDE the panel padding zone (closer to the corner) so the
     header content (title) is unaffected by the close affordance. */
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  transition: background ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'};

  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurface ?? '#E4E1ED'};
    &:hover {
      background: ${colors.surfaceContainerHigh ?? '#292932'};
    }
    &:focus-visible {
      outline: 2px solid ${colors.primary ?? '#C1C1FF'};
      outline-offset: 2px;
    }
  `}

  & > svg {
    width: 18px;
    height: 18px;
  }
`;

export const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* No padding — the panel's 28/36/24 padding is the only outer gutter.
     If a body needs additional gutters (e.g. a nested scroll region) the
     consumer adds them locally. */
`;

export const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  /* No padding, no border — sits flush inside the panel padding with a
     20px top margin separating it from the body. Matches the design's
     wf-details foot rule (margin-top 20px, gap 12px). */
  margin-top: 20px;
`;
